import type { FC } from 'react';

import type { FormikContextType } from 'formik';
import { connect as formikConnect, getIn } from 'formik';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose, mapProps } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';

import type { AutocompleteInputProps } from '.';
import { AutocompleteInput } from '.';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
});
const connector = connect(null, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;
/**
 * @TODO figure out type annotation through a series of HOCs
 */
export const AutoCompleteInputWired: FC<AutocompleteInputProps<Record<string, any>>> = compose(
  formikConnect,
  connector,
  mapProps(parentProps => {
    const {
      name,
      formik,
      itemToString,
      onSelectedItemChangeCallback,
      onInputValueChangeCallback,
      onBlur,
      dispatchSegmentTrack,
      ...rest
    } = parentProps as PropsFromRedux & AutocompleteInputProps<any> & { formik: FormikContextType<any> };

    const { values, touched, errors, handleChange, handleBlur: formikHandleBlur, setFieldValue } = formik;
    const error = getIn(touched, name) && getIn(errors, name);
    const value = getIn(values, name);

    const mergedHandleBlur = e => {
      formikHandleBlur(e);
      onBlur?.(e);
    };

    /**
     * This will normalize two shapes to a string.
     * - a response list item
     * - a newEntry item
     *
     * Return an empty string if there is no item.
     */
    const itemOrNewEntryToString = item => {
      if (!item) {
        return '';
      }
      return item.isNewEntry ? item.name : itemToString(item);
    };

    const onInputValueChange = (inputValue: string) => {
      setFieldValue(name, inputValue);
      onInputValueChangeCallback?.(inputValue);
    };

    const onSelectedItemChange = item => {
      const value = itemOrNewEntryToString(item);
      setFieldValue(name, value);
      onSelectedItemChangeCallback?.(item);
    };

    return {
      ...rest,
      name,
      itemToString: itemOrNewEntryToString,
      error,
      value,
      onChange: handleChange,
      onBlur: mergedHandleBlur,
      dispatchSegmentTrack,
      onInputValueChange,
      onSelectedItemChange,
    };
  })
)(AutocompleteInput);
